import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { FileText, ScanLine, Save, AlertTriangle, CheckCircle2, Loader } from 'lucide-react';
import { sepaApi } from '../../utils/api';
import SignatureButton from '../../components/Signature/SignatureButton';
import EnvoiDocumentButton from '../../components/EnvoiDocumentButton';

// Contrôle de la clé IBAN (modulo 97) côté écran, pour signaler une faute de frappe dès la saisie.
function ibanOk(v) {
  const s = (v || '').replace(/\s+/g, '').toUpperCase();
  if (!/^[A-Z]{2}\d{2}[A-Z0-9]{11,30}$/.test(s)) return false;
  const r = (s.slice(4) + s.slice(0, 4)).replace(/[A-Z]/g, c => String(c.charCodeAt(0) - 55));
  let mod = 0;
  for (const ch of r) mod = (mod * 10 + Number(ch)) % 97;
  return mod === 1;
}
const formatIban = (v) => (v || '').replace(/\s+/g, '').toUpperCase().replace(/(.{4})/g, '$1 ').trim();

/**
 * Mandat de prélèvement SEPA de l'enfant : coordonnées bancaires de la famille
 * (saisies ou lues depuis le RIB du dossier), PDF pré-rempli, envoi et signature.
 */
export default function MandatSepaCard({ enfantId, emailFamille }) {
  const [info, setInfo] = useState(null);
  const [form, setForm] = useState({ titulaire: '', adresse: '', iban: '', bic: '', type_paiement: 'recurrent', source_rib: '' });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await sepaApi.get(enfantId);
      setInfo(data);
      const m = data.mandat || {};
      setForm({
        titulaire: m.titulaire || data.defauts.titulaire || '',
        adresse: m.adresse || data.defauts.adresse || '',
        iban: formatIban(m.iban), bic: m.bic || '',
        type_paiement: m.type_paiement || 'recurrent', source_rib: m.source_rib || '',
      });
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Chargement impossible' }); }
  }, [enfantId]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const lireRib = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await sepaApi.lireRib(enfantId);
      setForm(f => ({ ...f, iban: formatIban(data.iban), bic: data.bic || f.bic, titulaire: data.titulaire || f.titulaire, source_rib: data.source }));
      setMsg({ type: 'ok', text: `IBAN lu dans « ${data.source} ». Vérifiez puis enregistrez.` });
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Lecture du RIB impossible' }); }
    setBusy(false);
  };

  const enregistrer = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await sepaApi.save(enfantId, form);
      setMsg({ type: 'ok', text: data.message });
      await load();
    } catch (err) { setMsg({ type: 'error', text: err.response?.data?.error || 'Enregistrement impossible' }); }
    setBusy(false);
  };

  if (!info) return <div className="text-sm text-gray-400 flex items-center gap-2"><Loader size={14} className="animate-spin" /> Chargement…</div>;

  const m = info.mandat;
  const icsManquant = !info.creancier.ics;
  const ibanSaisi = form.iban.replace(/\s+/g, '');
  const ibanFaux = ibanSaisi && !ibanOk(ibanSaisi);
  const pret = m && m.rum && !icsManquant;

  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-400">
        Mandat pré-rempli avec l'identité de la crèche (créancier : <strong>{info.creancier.raison_sociale}</strong>) et les coordonnées bancaires de la famille.
        L'IBAN peut être lu automatiquement depuis le RIB déposé dans la bibliothèque de l'enfant.
      </p>

      {icsManquant && (
        <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 rounded-lg px-3 py-2">
          <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
          <span>L'<strong>identifiant créancier SEPA (ICS)</strong> de cette crèche n'est pas renseigné : le mandat ne peut pas être généré.
            Un administrateur doit le renseigner dans <Link to="/marque" className="underline">Identité &amp; Charte</Link> (il est délivré par la banque de la crèche).</span>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="form-label">Titulaire du compte (débiteur)</label>
          <input className="form-input" value={form.titulaire} onChange={e => set('titulaire', e.target.value)} />
        </div>
        <div>
          <label className="form-label">Adresse du titulaire</label>
          <input className="form-input" value={form.adresse} onChange={e => set('adresse', e.target.value)} />
        </div>
        <div>
          <label className="form-label">IBAN</label>
          <input className={`form-input font-mono ${ibanFaux ? 'border-red-400' : ''}`} value={form.iban}
            onChange={e => set('iban', e.target.value)} onBlur={e => set('iban', formatIban(e.target.value))} placeholder="FR76 …" />
          {ibanFaux && <p className="text-xs text-red-500 mt-1">IBAN invalide : la clé de contrôle ne correspond pas.</p>}
          {form.source_rib && !ibanFaux && <p className="text-xs text-gray-400 mt-1">Lu dans « {form.source_rib} »</p>}
        </div>
        <div>
          <label className="form-label">BIC</label>
          <input className="form-input font-mono" value={form.bic} onChange={e => set('bic', e.target.value.toUpperCase())} placeholder="8 ou 11 caractères" />
        </div>
        <div className="md:col-span-2 flex items-center gap-5 text-sm">
          <span className="text-gray-500">Type de paiement</span>
          <label className="flex items-center gap-1.5"><input type="radio" checked={form.type_paiement === 'recurrent'} onChange={() => set('type_paiement', 'recurrent')} /> Récurrent (mensualités)</label>
          <label className="flex items-center gap-1.5"><input type="radio" checked={form.type_paiement === 'ponctuel'} onChange={() => set('type_paiement', 'ponctuel')} /> Ponctuel</label>
        </div>
      </div>

      {msg && (
        <div className={`text-sm rounded-lg px-3 py-2 flex items-center gap-2 ${msg.type === 'ok' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
          {msg.type === 'ok' ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />} {msg.text}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-xs text-gray-500">
          {m?.rum ? <>RUM : <span className="font-mono">{m.rum}</span></> : 'La référence du mandat (RUM) sera créée au premier enregistrement.'}
          {m?.date_signature && <> · <span className="text-emerald-600">signé le {m.date_signature.split('-').reverse().join('/')}</span></>}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" onClick={lireRib} disabled={busy} className="btn-secondary"><ScanLine size={15} /> Lire le RIB du dossier</button>
          <button type="button" onClick={enregistrer} disabled={busy || ibanFaux} className="btn-secondary"><Save size={15} /> Enregistrer</button>
          {pret && <a href={sepaApi.mandatPdfUrl(enfantId)} target="_blank" rel="noopener noreferrer" className="btn-primary"><FileText size={15} /> Générer le mandat</a>}
          {pret && <EnvoiDocumentButton type="mandat_sepa" enfantId={enfantId} refId={enfantId} destinataireDefaut={emailFamille} label="Envoyer" />}
          {pret && m.iban && <SignatureButton type="mandat_sepa" refId={enfantId} email={emailFamille} nom={form.titulaire} />}
        </div>
      </div>
    </div>
  );
}
